<template>
    <div
        class="table-cell"
        :class="{
            'placeholder-cell': isPlaceholder,
            'text-right': align === 'right',
            'text-center': align === 'center'
        }"
        :style="cellStyle"
        @click="$emit('click')">
        <div
            v-if="!isPlaceholder"
            class="cell-content">
            <slot>
                {{ value }}
            </slot>
        </div>
        <div
            v-else
            class="placeholder-content" />
    </div>
</template>

<script>
export default {
    props: {
        column: {
            type: String,
            required: true
        },
        value: {
            type: null,
            default: undefined
        },
        align: {
            type: String,
            default: 'left',
            validator: value => ['left', 'center', 'right'].includes(value)
        },
        width: {
            type: Number,
            default: undefined
        },
        shouldGrow: {
            type: Boolean,
            required: true
        },
        height: {
            type: Number,
            required: true
        },
        isPlaceholder: {
            type: Boolean,
            required: true
        }
    },
    computed: {
        cellStyle() {
            const baseStyle = {
                height: `${this.height}px`
            };

            if (this.shouldGrow) {
                return {
                    ...baseStyle,
                    flex: '1 1 auto',
                    minWidth: '3.75rem',
                    maxWidth: '100%' // Prevent infinite growth
                };
            }

            // Other columns have fixed widths
            const width = this.width || 60;

            // Actions column should have a hard minimum width to ensure edit button is always visible
            if (this.column === 'actions') {
                const finalWidth = Math.max(width, 55);
                return {
                    ...baseStyle,
                    flex: `0 0 ${finalWidth}px`,
                    width: `${finalWidth}px`,
                    minWidth: `${finalWidth}px`,
                    maxWidth: `${finalWidth}px`
                };
            }

            return {
                ...baseStyle,
                flex: `0 0 ${width}px`,
                width: `${width}px`,
                minWidth: `${width}px`,
                maxWidth: `${width}px`
            };
        }
    }
};
</script>

<style scoped>
.table-cell {
    display: flex;
    align-items: center;
    transition: background-color 0.1s ease;
    cursor: pointer;
    border-right: 0.1rem solid var(--app-table-border, #e0e0e0);
    border-bottom: 0.1rem solid var(--app-table-border, #e0e0e0);
    padding: 0.5rem 0.5rem 0.5rem 0.5rem;
    overflow: hidden;
    font-size: 0.95rem;
}

.table-cell:last-child {
    border-right: none;
}

.table-cell:hover {
    background-color: rgba(0, 0, 0, 0.02);
}

.placeholder-cell {
    cursor: default;
    background-color: rgba(0, 0, 0, 0.02);
}

.cell-content {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cell-content >>> * {
    display: flex;
    flex-direction: row;
    gap: 0.3em;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.text-right .cell-content {
    text-align: right;
}

.text-center .cell-content {
    text-align: center;
}

.placeholder-content {
    animation: shimmer 1.5s infinite;
    border-radius: var(--app-border-radius, 0.25rem);
    background: linear-gradient(90deg, rgba(0, 0, 0, 0.05) 25%, rgba(0, 0, 0, 0.02) 50%, rgba(0, 0, 0, 0.05) 75%);
    background-size: 200% 100%;
    width: 60%;
    height: 1rem;
}

@keyframes shimmer {
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
}

</style>
